import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormState } from 'react-final-form';
import PriceWithCurrency from '../../../../../../components/atoms/PriceWithCurrency';
import { useCitiesList } from '../../../../../../hooks/useCitiesList';
import { useEntitySelectItems, useFeesProfileData } from '../../OrderForm/hooks';
import { OrderFormValues } from '../../../types';
import {
  StyledReviewSection,
  StyledReviewSectionTitle,
  StyledReviewRow,
  StyledReviewLabel,
  StyledReviewValue,
  StyledReviewTotalRow,
} from '../styles';

const ReviewSummary: React.FC = () => {
  const { values } = useFormState<OrderFormValues>();
  const entitySelectItems = useEntitySelectItems();
  const { list: citiesOptions } = useCitiesList();
  const { deliveryChargeOptions, deliveryFee, currency: profileCurrency } =
    useFeesProfileData();

  const {
    entity,
    name,
    phoneNumber,
    secondPhoneNumber,
    externalReference,
    city,
    address,
    notes,
    deliveryRegion,
    products = [],
    total,
    amountIncludeDeliveryCharge,
  } = values;

  const displayCurrency = values.currency ?? profileCurrency;
  const entityLabel =
    entitySelectItems.find(({ value }) => value === entity)?.label ?? entity;
  const cityLabel =
    citiesOptions.find(({ value }) => value === city)?.label ?? city;
  const deliveryRegionLabel = deliveryChargeOptions.find(
    ({ value }) => value === deliveryRegion
  )?.label;
  const grandTotal =
    (total ?? 0) + (amountIncludeDeliveryCharge ? 0 : deliveryFee ?? 0);

  const dash = '—';

  return (
    <div>
      <StyledReviewSection>
        <StyledReviewSectionTitle>
          <FormattedMessage id='orderWizard.review.store' defaultMessage='Store' />
        </StyledReviewSectionTitle>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage id='forms.fields.entity' defaultMessage='Entity' />
          </StyledReviewLabel>
          <StyledReviewValue>{entityLabel || dash}</StyledReviewValue>
        </StyledReviewRow>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='forms.fields.currency'
              defaultMessage='Currency'
            />
          </StyledReviewLabel>
          <StyledReviewValue>{displayCurrency ?? dash}</StyledReviewValue>
        </StyledReviewRow>
      </StyledReviewSection>

      <StyledReviewSection>
        <StyledReviewSectionTitle>
          <FormattedMessage
            id='orderWizard.review.customer'
            defaultMessage='Customer'
          />
        </StyledReviewSectionTitle>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='forms.fields.customer'
              defaultMessage='Customer'
            />
          </StyledReviewLabel>
          <StyledReviewValue>{name || dash}</StyledReviewValue>
        </StyledReviewRow>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='forms.fields.phoneNumber'
              defaultMessage='Phone Number'
            />
          </StyledReviewLabel>
          <StyledReviewValue>
            {[phoneNumber, secondPhoneNumber].filter(Boolean).join(' / ') ||
              dash}
          </StyledReviewValue>
        </StyledReviewRow>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='forms.fields.receiptNumber'
              defaultMessage='Receipt Number'
            />
          </StyledReviewLabel>
          <StyledReviewValue>{externalReference || dash}</StyledReviewValue>
        </StyledReviewRow>
      </StyledReviewSection>

      <StyledReviewSection>
        <StyledReviewSectionTitle>
          <FormattedMessage
            id='orderWizard.review.delivery'
            defaultMessage='Delivery'
          />
        </StyledReviewSectionTitle>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage id='forms.fields.city' defaultMessage='City' />
          </StyledReviewLabel>
          <StyledReviewValue>{cityLabel || dash}</StyledReviewValue>
        </StyledReviewRow>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='forms.fields.address'
              defaultMessage='Address'
            />
          </StyledReviewLabel>
          <StyledReviewValue>{address || dash}</StyledReviewValue>
        </StyledReviewRow>
        {notes && (
          <StyledReviewRow>
            <StyledReviewLabel>
              <FormattedMessage
                id='forms.fields.addressNotes'
                defaultMessage='Address Notes'
              />
            </StyledReviewLabel>
            <StyledReviewValue>{notes}</StyledReviewValue>
          </StyledReviewRow>
        )}
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='delivery.deliveryCharge'
              defaultMessage='Delivery charge'
            />
          </StyledReviewLabel>
          <StyledReviewValue>{deliveryRegionLabel ?? dash}</StyledReviewValue>
        </StyledReviewRow>
      </StyledReviewSection>

      <StyledReviewSection>
        <StyledReviewSectionTitle>
          <FormattedMessage id='orderWizard.review.items' defaultMessage='Items' />
        </StyledReviewSectionTitle>
        {products.length > 0 ? (
          products.map(({ idCol, productCol, quantityCol, priceCol }) => (
            <StyledReviewRow key={idCol}>
              <StyledReviewLabel>
                {productCol} × {quantityCol}
              </StyledReviewLabel>
              <StyledReviewValue>
                <PriceWithCurrency
                  price={priceCol * quantityCol}
                  currency={displayCurrency}
                />
              </StyledReviewValue>
            </StyledReviewRow>
          ))
        ) : (
          <StyledReviewRow>
            <StyledReviewLabel>
              <FormattedMessage
                id='products.noProductsAdded'
                defaultMessage='No products added'
              />
            </StyledReviewLabel>
          </StyledReviewRow>
        )}
      </StyledReviewSection>

      <StyledReviewSection>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='orderWizard.review.subtotal'
              defaultMessage='Subtotal'
            />
          </StyledReviewLabel>
          <StyledReviewValue>
            <PriceWithCurrency price={total ?? 0} currency={displayCurrency} />
          </StyledReviewValue>
        </StyledReviewRow>
        <StyledReviewRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='delivery.deliveryCharge'
              defaultMessage='Delivery charge'
            />
          </StyledReviewLabel>
          <StyledReviewValue>
            {amountIncludeDeliveryCharge ? (
              <FormattedMessage
                id='orderWizard.review.included'
                defaultMessage='Included in total'
              />
            ) : (
              <PriceWithCurrency
                price={deliveryFee}
                currency={displayCurrency}
              />
            )}
          </StyledReviewValue>
        </StyledReviewRow>
        <StyledReviewTotalRow>
          <StyledReviewLabel>
            <FormattedMessage
              id='products.grandTotal'
              defaultMessage='Grand Total'
            />
          </StyledReviewLabel>
          <StyledReviewValue>
            <PriceWithCurrency price={grandTotal} currency={displayCurrency} />
          </StyledReviewValue>
        </StyledReviewTotalRow>
      </StyledReviewSection>
    </div>
  );
};

export default ReviewSummary;
